/**
 * Mutation + query hooks for the attention/communication layer (ADR-0057).
 *
 *   useCreateTask()        → POST  /api/operations/tasks       (watch | to-do)
 *   useTaskAction()        → PATCH /api/operations/tasks/:id   (close loop)
 *   useTaskMessages(id)    → GET   /api/operations/tasks/:id/messages
 *   useAddMessage(id)      → POST  /api/operations/tasks/:id/messages
 *
 * Writes invalidate the ["surface"] query so a new watch/to-do, a status flip,
 * or a confirm reflects on the next render — a broad-but-cheap invalidation
 * (two operators, small surface).
 *
 * Slice D — Dashboard rows fire the same hooks. `useTaskAction` and
 * `useAddMessage` also invalidate `["dashboard"]` (status / row metadata /
 * threadCount) and `["dashboard-count"]` (sidebar badge), so the Dashboard
 * and the sidebar stay coherent when either surface drives an action.
 *
 * Spec: docs/port-notes/surface.md (evicted 2026-08-29, #775 round 10) · ADR-0082 (Dashboard)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  TaskMessagesResponseSchema,
  type CreateTaskRequest,
  type Task,
  type TaskActionRequest,
  type TaskMessage,
  type TaskMessagesResponse,
} from "@command-center/shared";
import { apiGet, apiPatch, apiPost } from "./api";

const messagesKey = (taskId: string) => ["task-messages", taskId] as const;

/** Create a watch or a to-do. */
export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation<Task, Error, CreateTaskRequest>({
    mutationFn: (req) => apiPost<Task>("/api/operations/tasks", req),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["surface"] });
      // Slice E — a new to-do/watch lands on the Dashboard immediately
      // and bumps the sidebar count badge.
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard-count"] });
      // Slice I — a new entity-bound task lands on the Firm / User Detail
      // Panel's Tasks section. The `["firm-success"]` prefix covers the
      // firms roster (tasks ride the bulk payload) AND any open user-detail
      // query (per-id fetch keyed under `["firm-success", "user", id]`).
      // Broad-but-cheap: both queries already poll on a 30s cadence and the
      // payloads are bounded (tens of firms / ~120 users).
      void queryClient.invalidateQueries({ queryKey: ["firm-success"] });
    },
  });
}

/** Drive the close loop: mark-done | send-back | clear | set-commit (+ optional note). */
export function useTaskAction() {
  const queryClient = useQueryClient();
  return useMutation<Task, Error, { id: string } & TaskActionRequest>({
    mutationFn: ({ id, ...body }) =>
      apiPatch<Task>(`/api/operations/tasks/${encodeURIComponent(id)}`, body),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["surface"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard-count"] });
      void queryClient.invalidateQueries({ queryKey: messagesKey(vars.id) });
      // Slice I — close-loop actions (mark-done / send-back / clear /
      // set-commit / snooze / unsnooze) flip the row's state on whatever
      // entity panel surfaces it; same broad invalidation rationale as
      // useCreateTask (see comment there).
      void queryClient.invalidateQueries({ queryKey: ["firm-success"] });
    },
  });
}

/** A task's thread. Only fetched when the thread is open (`enabled`). */
export function useTaskMessages(taskId: string, enabled: boolean) {
  return useQuery<TaskMessagesResponse, Error>({
    queryKey: messagesKey(taskId),
    enabled,
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `/api/operations/tasks/${encodeURIComponent(taskId)}/messages`,
      );
      const parsed = TaskMessagesResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/operations/tasks/:id/messages failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
  });
}

/** Append a message to a task's thread. */
export function useAddMessage(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation<TaskMessage, Error, { body: string }>({
    mutationFn: (body) =>
      apiPost<TaskMessage>(
        `/api/operations/tasks/${encodeURIComponent(taskId)}/messages`,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: messagesKey(taskId) });
      void queryClient.invalidateQueries({ queryKey: ["surface"] });
      // Dashboard rows show threadCount; a new message bumps it.
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}
